import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Tooltip, notification } from 'antd';
import ChatService from './ChatService';
import { getLoggedInUser } from 'utilities/helpers';
import styles from './styles.module.less';
import chatClose from '@/common/assets/images/chatClose.svg';
import { FiSend } from 'react-icons/fi';
// import { FaCheck, FaCheckDouble } from 'react-icons/fa';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { FaChevronDown } from 'react-icons/fa';
import online from '@/common/assets/icons/online.svg';
import offline from '@/common/assets/icons/offline.svg';
import { useSelector } from 'react-redux';

const ChatSystem = ({ currentUserId: propUserId, closeChat }) => {
  const [currentUserId, setCurrentUserId] = useState(propUserId || null);
  const [isConnected, setIsConnected] = useState(false);
  // const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [userStatuses, setUserStatuses] = useState({});
  const [fetchingStatuses, setFetchingStatuses] = useState(false);
  const statusPollingInterval = useRef(null);
  const lastStatusUpdateTime = useRef({});

  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const cleanupFunctions = useRef([]);

  const { user_profile_data } = useSelector((state) => state.listDashboard);

  console.log('user_profile_data', user_profile_data);

  // Get current user if not provided as prop
  useEffect(() => {
    if (!propUserId) {
      const fetchUser = async () => {
        try {
          const loggedInUser = await getLoggedInUser();
          const user_details = loggedInUser?.user_details;

          const profileId =
            user_profile_data?.profile_id || user_details?.profile_id;

          if (profileId) {
            setCurrentUserId(profileId);
          } else {
            setError(
              'Profile not found. Please create your profile to continue.'
            );
            setLoading(false);
          }
        } catch (err) {
          console.error('Failed to fetch user:', err);
          setError(
            'An error occurred while retrieving your account details. Please try again later.'
          );
          setLoading(false);
        }
      };
      fetchUser();
    }
  }, [propUserId, user_profile_data]);

  // Connection status handler
  const handleConnectionStatus = useCallback((isConnected, error) => {
    console.log('Connection status changed:', { isConnected, error });
    const connected = isConnected === true;
    setIsConnected(connected);
    // setConnectionStatus(
    //   connected ? 'connected' : error ? 'error' : 'disconnected'
    // );
    if (!connected && error) {
      setError(`Connection error: ${error?.message || 'Unknown error'}`);
    } else if (connected) {
      setError((prevError) =>
        prevError?.includes?.('connection') ? null : prevError
      );
    }
  }, []);

  // Message handlers
  const handleNewMessage = useCallback(
    (message) => {
      console.log(
        'New message received:',
        message,
        'Current user:',
        currentUserId
      );
      // Determine which user profile ID is relevant for this message
      const relevantProfileId =
        String(message.senderId) === String(currentUserId)
          ? message.recipientId
          : message.senderId;

      // Update their status timestamp as well
      if (String(message.senderId) !== String(currentUserId)) {
        setUserStatuses((prev) => ({
          ...prev,
          [message.senderId]: true
        }));
        lastStatusUpdateTime.current[message.senderId] = Date.now();
      }

      // Check if this message is visible in the currently open conversation
      const isVisibleInCurrentConversation =
        selectedConversation &&
        String(selectedConversation.profile_id) === String(relevantProfileId);
      // Add message to current conversation if it's visible
      if (isVisibleInCurrentConversation) {
        console.log(
          'Message belongs to current conversation, adding to messages'
        );
        setMessages((prevMessages) => [...prevMessages, message]);
        // If it's an incoming message (not sent by current user), mark it as read immediately
        if (String(message.senderId) !== String(currentUserId)) {
          console.log('Marking conversation as read because the chat is open');
          ChatService.markConversationRead(message.senderId);
        }
      } else {
        console.log('Message does not belong to current conversation');
      }

      // Update conversations list
      setConversations((prevConversations) => {
        const existingConvIndex = prevConversations.findIndex(
          (conv) => String(conv.profile_id) === String(relevantProfileId)
        );

        if (existingConvIndex >= 0) {
          // Update existing conversation
          const updatedConversations = [...prevConversations];
          // IMPORTANT: If the message is visible in the currently open chat window
          // AND it's an incoming message, set unread count to 0
          // Otherwise, increment the unread count
          const shouldIncrementUnread =
            !isVisibleInCurrentConversation &&
            String(message.senderId) !== String(currentUserId);
          updatedConversations[existingConvIndex] = {
            ...updatedConversations[existingConvIndex],
            lastMessage: message.message,
            lastMessageTime: message.timestamp,
            unreadCount: isVisibleInCurrentConversation
              ? 0 // Reset to 0 if conversation is open
              : shouldIncrementUnread
              ? (updatedConversations[existingConvIndex].unreadCount || 0) + 1
              : updatedConversations[existingConvIndex].unreadCount || 0
          };

          // Move this conversation to the top
          const [conv] = updatedConversations.splice(existingConvIndex, 1);
          return [conv, ...updatedConversations];
        } else {
          // Create new conversation entry for a new contact
          const newConversation = {
            profile_id: relevantProfileId,
            first_name: `User ${relevantProfileId}`,
            last_name: '',
            ProfileImages: [],
            lastMessage: message.message,
            lastMessageTime: message.timestamp,
            unreadCount:
              String(message.senderId) !== String(currentUserId) ? 1 : 0
          };

          return [newConversation, ...prevConversations];
        }
      });
    },
    [selectedConversation, currentUserId]
  );

  // Handle user status response - improved to track last update time
  const handleUserStatusResponse = useCallback((data) => {
    console.log('User status response received in component:', data);
    if (data && data.userId) {
      // Update the status
      setUserStatuses((prev) => ({
        ...prev,
        [data.userId]: data.isOnline
      }));

      // Track when this status was last updated
      lastStatusUpdateTime.current[data.userId] = Date.now();

      // If this data contains otherUserStatus, update that as well
      if (data.otherUserStatus !== undefined && data.otherUserId) {
        setUserStatuses((prev) => ({
          ...prev,
          [data.otherUserId]: data.otherUserStatus
        }));
        lastStatusUpdateTime.current[data.otherUserId] = Date.now();
      }

      // Update conversations list to reflect the new status
      setConversations((prevConversations) =>
        prevConversations.map((conv) =>
          String(conv.profile_id) === String(data.userId)
            ? { ...conv, isOnline: data.isOnline }
            : conv
        )
      );
    }
  }, []);

  // Status change handler with improved logging and UI updates
  const handleUserStatusChange = useCallback((data) => {
    console.log('User status change received in component:', data);
    if (data && data.userId) {
      // Update our status tracking
      setUserStatuses((prev) => ({
        ...prev,
        [data.userId]: data.isOnline
      }));

      // Update the timestamp
      lastStatusUpdateTime.current[data.userId] = Date.now();

      // Update conversations list to reflect status change
      setConversations((prevConversations) =>
        prevConversations.map((conv) =>
          String(conv.profile_id) === String(data.userId)
            ? { ...conv, isOnline: data.isOnline }
            : conv
        )
      );

      console.log(
        `User ${data.userId} is now ${data.isOnline ? 'online' : 'offline'}`
      );
    }
  }, []);

  const handleConversationHistory = useCallback(
    (data) => {
      console.log('Received conversation history:', data);
      if (!data || !Array.isArray(data.messages)) {
        console.error('Invalid conversation history data:', data);
        setLoadingMessages(false);
        return;
      }
      // Process messages to ensure correct sent/received identification
      const processedMessages = data.messages.map((message) => ({
        ...message,
        // Convert IDs to strings for consistent comparison
        senderId: String(message.senderId),
        recipientId: String(message.recipientId),
        // Make sure isRead is properly processed
        isRead: Boolean(message.isRead),
        // Set flag for UI rendering
        isSentByCurrentUser: String(message.senderId) === String(currentUserId)
      }));
      console.log('Processed messages:', processedMessages);
      // Reverse to show newest at the bottom
      setMessages(processedMessages.reverse());
      setLoadingMessages(false);
    },
    [currentUserId]
  );

  const handleUserConversations = useCallback((data) => {
    console.log('Received user conversations:', data);

    // Transform the data to add the missing properties needed by the chat UI
    const transformedConversations = data.map((conversation) => ({
      ...conversation,
      profile_id: conversation.profile_id, // Make sure this key exists
      lastMessage: conversation.lastMessage ?? '', // Initialize with empty values
      lastMessageTime: conversation.lastMessageTime ?? null,
      unreadCount: conversation.unreadCount || 0
    }));
    console.log('Transformed conversations:', transformedConversations);
    setConversations(transformedConversations || []);
    console.log('conversation', data);
    // Extract any status information that might be included
    if (Array.isArray(data)) {
      const statusUpdates = {};
      data.forEach((conversation) => {
        if (conversation.isOnline !== undefined) {
          statusUpdates[conversation.profile_id] = conversation.isOnline;
        }
      });

      if (Object.keys(statusUpdates).length > 0) {
        setUserStatuses((prev) => ({
          ...prev,
          ...statusUpdates
        }));
      }
    }
  }, []);

  // 🔄 Log and perform side effects when conversations update
  useEffect(() => {
    if (conversations.length > 0) {
      console.log('Updated conversations:', conversations);
    }
  }, [conversations]);

  const handleMessageSent = useCallback(
    (data) => {
      // Update messages list to replace temporary message with confirmed one
      console.log('Message sent:', data);
      setMessages((prevMessages) => {
        // Find any temporary messages that match this content
        const tempMessageIndex = prevMessages.findIndex(
          (msg) => msg.isPending && msg.message === data.message
        );

        if (tempMessageIndex >= 0) {
          // Replace the temporary message with the confirmed one
          const updatedMessages = [...prevMessages];
          updatedMessages[tempMessageIndex] = {
            id: data.id,
            senderId: currentUserId,
            recipientId: data.recipientId,
            message: data.message,
            timestamp: data.timestamp,
            isRead: false,
            isPending: false
          };
          return updatedMessages;
        }

        // If we didn't find a matching temporary message,
        // add it directly (fallback - should be rare)
        if (
          selectedConversation &&
          String(selectedConversation.profile_id) === String(data.recipientId)
        ) {
          return [
            ...prevMessages,
            {
              id: data.id,
              senderId: currentUserId,
              recipientId: data.recipientId,
              message: data.message,
              timestamp: data.timestamp,
              isRead: false
            }
          ];
        }
        return prevMessages;
      });

      // Update conversations list
      setConversations((prevConversations) => {
        // Use profile_id instead of userId for consistency
        const convIndex = prevConversations.findIndex(
          (conv) => String(conv.profile_id) === String(data.recipientId)
        );

        if (convIndex >= 0) {
          const updatedConversations = [...prevConversations];
          updatedConversations[convIndex] = {
            ...updatedConversations[convIndex],
            lastMessage: data.message,
            lastMessageTime: data.timestamp
          };

          // Move this conversation to the top if it's not already
          if (convIndex > 0) {
            const [conv] = updatedConversations.splice(convIndex, 1);
            return [conv, ...updatedConversations];
          }
          return updatedConversations;
        }
        return prevConversations;
      });
    },
    [selectedConversation, currentUserId]
  );

  const handleMessageError = useCallback((error) => {
    setError(`Message error: ${error.error || 'Unknown error'}`);
    setTimeout(() => setError(null), 5000); // Clear error after 5 seconds
  }, []);

  const handleConversationMarkedRead = useCallback(
    (data) => {
      console.log('Conversation marked read event:', data);
      if (data.success) {
        // If the current user is viewing the conversation that was marked as read
        if (
          selectedConversation &&
          String(selectedConversation.profile_id) === String(data.userId)
        ) {
          // No need to update anything - this is for messages the current user received
        }
        // If another user has read messages sent by current user
        else if (
          data.otherUserId &&
          String(data.otherUserId) === String(currentUserId)
        ) {
          // Update read status for messages in the conversation with this user
          setMessages((prevMessages) =>
            prevMessages.map((msg) =>
              // Only update messages sent by current user to the user who marked as read
              String(msg.senderId) === String(currentUserId) &&
              String(msg.recipientId) === String(data.userId)
                ? { ...msg, isRead: true }
                : msg
            )
          );
        }
      }
    },
    [selectedConversation, currentUserId]
  );

  useEffect(() => {
    if (error && !isConnected) {
      showErrorNotification();
    }
  }, [error, isConnected]);

  const showErrorNotification = () => {
    notification.info({
      message: 'Connection Error',
      description: error,
      placement: 'bottomRight',
      duration: 5 // Won't auto-dismiss
    });
  };

  //showing loading notification
  useEffect(() => {
    if (loading) {
      // showLoadingNotification();
    }
  }, [loading]);
  // const showLoadingNotification = () => {
  //   notification.info({
  //     message: 'Loading chat system...',
  //     description: error,
  //     placement: 'bottomRight',
  //     duration: 1 // Won't auto-dismiss
  //   });
  // };

  // New handler for message read events
  // const handleMessageRead = useCallback(
  //   (data) => {
  //     console.log('Message read event received:', data);

  //     // Make sure we have the necessary data
  //     if (!data || !data.senderId || !data.recipientId) return;

  //     // Check if the current user is the sender whose messages were read
  //     if (String(data.senderId) === String(currentUserId)) {
  //       // Update all messages sent by the current user to this recipient
  //       setMessages((prevMessages) =>
  //         prevMessages.map((msg) => {
  //           // Update only messages sent by current user to the recipient who read them
  //           // that haven't been marked as read yet
  //           if (
  //             String(msg.senderId) === String(currentUserId) &&
  //             String(msg.recipientId) === String(data.recipientId) &&
  //             !msg.isRead
  //           ) {
  //             return { ...msg, isRead: true };
  //           }
  //           return msg;
  //         })
  //       );
  //     }
  //   },
  //   [currentUserId]
  // );
  const handleMessageRead = useCallback(
    (data) => {
      console.log('Message read event received:', data);
      // Make sure we have the necessary data
      if (!data || !data.senderId || !data.recipientId) return;
      // Check if the current user is the sender whose messages were read
      if (String(data.senderId) === String(currentUserId)) {
        console.log(
          'Updating read status for messages sent to:',
          data.recipientId
        );
        // Update all messages sent by the current user to this recipient
        setMessages((prevMessages) =>
          prevMessages.map((msg) => {
            // Update only messages sent by current user to the recipient who read them
            // that haven't been marked as read yet
            if (
              String(msg.senderId) === String(currentUserId) &&
              String(msg.recipientId) === String(data.recipientId) &&
              !msg.isRead
            ) {
              console.log('Marking message as read:', msg.id, msg.message);
              return { ...msg, isRead: true };
            }
            return msg;
          })
        );
      }
    },
    [currentUserId]
  );

  // const handleUserStatusResponse = useCallback((data) => {
  //   console.log('User status response received in component:', data);
  //   if (data && data.userId) {
  //     setUserStatuses((prev) => ({
  //       ...prev,
  //       [data.userId]: data.isOnline
  //     }));

  //     // If this data contains otherUserStatus, update that as well
  //     if (data.otherUserStatus !== undefined && data.otherUserId) {
  //       setUserStatuses((prev) => ({
  //         ...prev,
  //         [data.otherUserId]: data.otherUserStatus
  //       }));
  //     }
  //   }
  // }, []);

  // Add this new handler function
  // const handleUserStatusChange = useCallback((data) => {
  //   console.log('User status change received in component:', data);
  //   if (data && data.userId) {
  //     setUserStatuses((prev) => ({
  //       ...prev,
  //       [data.userId]: data.isOnline
  //     }));

  //     // If this user is in our conversations list, update that too
  //     setConversations((prevConversations) =>
  //       prevConversations.map((conv) =>
  //         String(conv.profile_id) === String(data.userId)
  //           ? { ...conv, isOnline: data.isOnline }
  //           : conv
  //       )
  //     );
  //   }
  // }, []);

  // Initialize chat connection
  useEffect(() => {
    const connectChat = async () => {
      if (!currentUserId) return;

      try {
        setLoading(true);
        setError(null);

        console.log('Connecting chat...');
        console.log('Connecting chat with user ID:', currentUserId);
        const apiUrl =
          process.env.REACT_APP_API_URL2 ||
          'https://api.samskrutimatrimony.com/';

        await ChatService.initialize(apiUrl);

        console.log('Connected!');
        setIsConnected(true);

        // Register event listeners
        const cleanup1 = ChatService.onNewMessage(handleNewMessage);
        const cleanup2 = ChatService.onConversationHistory(
          handleConversationHistory
        );
        const cleanup3 = ChatService.onUserConversations(
          handleUserConversations
        );
        const cleanup4 = ChatService.onMessageSent(handleMessageSent);
        const cleanup5 = ChatService.onMessageError(handleMessageError);
        const cleanup6 = ChatService.onConversationMarkedRead(
          handleConversationMarkedRead
        );
        const cleanup7 = ChatService.onConnectionStatus(handleConnectionStatus);
        // Add the new event listener for message read events (if implemented in ChatService)
        const cleanup8 = ChatService.onMessageRead
          ? ChatService.onMessageRead(handleMessageRead)
          : () => {};
        const cleanup9 = ChatService.onUserStatusResponse(
          handleUserStatusResponse
        );
        const cleanup10 = ChatService.onUserStatusChange(
          handleUserStatusChange
        );

        cleanupFunctions.current = [
          cleanup1,
          cleanup2,
          cleanup3,
          cleanup4,
          cleanup5,
          cleanup6,
          cleanup7,
          cleanup8,
          cleanup9,
          cleanup10
        ];

        // Load user conversations
        ChatService.getUserConversations();
      } catch (err) {
        console.error('Chat initialization error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (currentUserId) {
      connectChat();
    }

    // Cleanup on unmount
    return () => {
      cleanupFunctions.current.forEach(
        (cleanup) => cleanup && typeof cleanup === 'function' && cleanup()
      );
      ChatService.disconnect();
    };
  }, [
    currentUserId,
    handleNewMessage,
    handleConversationHistory,
    handleUserConversations,
    handleMessageSent,
    handleMessageError,
    handleConversationMarkedRead,
    handleConnectionStatus,
    handleMessageRead,
    handleUserStatusResponse,
    handleUserStatusChange
  ]);

  // Process online status from loaded conversations and fetch updates
  useEffect(() => {
    const processAndFetchUserStatuses = async () => {
      if (!conversations.length || !isConnected || fetchingStatuses) return;

      setFetchingStatuses(true);

      try {
        // First, extract any status information already included in the conversations data
        const statusUpdates = {};
        conversations.forEach((conversation) => {
          if (conversation.isOnline !== undefined) {
            statusUpdates[conversation.profile_id] = conversation.isOnline;
            // Update the timestamp for this status
            lastStatusUpdateTime.current[conversation.profile_id] = Date.now();
          }
        });

        // Update our status tracking with these initial values
        if (Object.keys(statusUpdates).length > 0) {
          console.log(
            'Extracted initial status data from conversations:',
            statusUpdates
          );
          setUserStatuses((prev) => ({
            ...prev,
            ...statusUpdates
          }));
        }

        // Then request fresh status updates to ensure accuracy
        console.log('Fetching fresh status for all users in conversations');
        conversations.forEach((conversation) => {
          // Only request status if we haven't received an update recently
          const lastUpdate =
            lastStatusUpdateTime.current[conversation.profile_id] || 0;
          const timeSinceLastUpdate = Date.now() - lastUpdate;

          // If it's been more than 30 seconds since the last update, request a fresh one
          if (timeSinceLastUpdate > 30000) {
            ChatService.getUserStatus(conversation.profile_id);
          }
        });
      } catch (err) {
        console.error('Error processing and fetching user statuses:', err);
      } finally {
        setFetchingStatuses(false);
      }
    };

    processAndFetchUserStatuses();
  }, [conversations, isConnected, fetchingStatuses]);

  // Scroll to bottom when messages update
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Mark conversation as read when selected
  useEffect(() => {
    if (selectedConversation) {
      console.log(
        'Selected conversation changed, marking as read:',
        selectedConversation.profile_id
      );
      // Mark conversation as read when selected
      ChatService.markConversationRead(selectedConversation.profile_id);
      // Update the unread count in conversations list immediately in the UI
      setConversations((prevConversations) =>
        prevConversations.map((conv) =>
          String(conv.profile_id) === String(selectedConversation.profile_id)
            ? { ...conv, unreadCount: 0 }
            : conv
        )
      );
    }
  }, [selectedConversation]);

  useEffect(() => {
    return () => {
      // Make sure to clean up ALL socket listeners when component unmounts
      if (ChatService && typeof ChatService.disconnect === 'function') {
        ChatService.disconnect();
      }
      // Clear any cleanup functions
      if (cleanupFunctions.current) {
        cleanupFunctions.current.forEach((cleanup) => {
          if (typeof cleanup === 'function') {
            cleanup();
          }
        });
        cleanupFunctions.current = [];
      }
    };
  }, []);

  useEffect(() => {
    // Skip if no selected conversation
    if (!selectedConversation) return;
    // Find the selected conversation in the conversations list
    const currentConvInList = conversations.find(
      (conv) =>
        String(conv.profile_id) === String(selectedConversation.profile_id)
    );
    // If we have both selectedConversation and a matching conversation in the list
    if (selectedConversation && currentConvInList) {
      const lastMessage =
        messages.length > 0 ? messages[messages.length - 1] : null;
      // IMPORTANT: Always reset unread count to 0 in the UI when a conversation is selected
      // This is critical for fixing the persistent unread indicator issue
      if (currentConvInList.unreadCount > 0) {
        console.log(
          'Resetting unread count for active conversation:',
          selectedConversation.profile_id
        );
        setConversations((prevConversations) =>
          prevConversations.map((conv) =>
            String(conv.profile_id) === String(selectedConversation.profile_id)
              ? { ...conv, unreadCount: 0 }
              : conv
          )
        );
        // Also mark as read on the server
        ChatService.markConversationRead(selectedConversation.profile_id);
      }
      // Check if we need to refresh based on timestamp or unread count
      const needsRefresh =
        // No messages loaded yet but there's a last message in the list
        (messages.length === 0 && currentConvInList.lastMessage) ||
        // Or we have messages but the list shows a newer timestamp
        (lastMessage &&
          currentConvInList.lastMessageTime &&
          new Date(currentConvInList.lastMessageTime) >
            new Date(lastMessage.timestamp));
      if (needsRefresh) {
        console.log(
          'Detected that conversation needs refresh:',
          selectedConversation.profile_id,
          'Last message time in list:',
          currentConvInList.lastMessageTime,
          'Last message time in chat:',
          lastMessage?.timestamp
        );
        // Refresh the conversation data
        ChatService.getConversation(selectedConversation.profile_id);
      }
    }
  }, [selectedConversation, conversations, messages]);

  useEffect(() => {
    console.log('Current conversation:', selectedConversation);
    console.log('Current messages:', messages);
  }, [selectedConversation, messages]);

  useEffect(() => {
    // If we have messages visible in the current conversation and they're not our own,
    // make sure they're marked as read
    if (selectedConversation && messages.length > 0) {
      const hasUnreadMessages = messages.some(
        (msg) => String(msg.senderId) !== String(currentUserId) && !msg.isRead
      );
      if (hasUnreadMessages) {
        console.log(
          'Marking visible messages as read in conversation:',
          selectedConversation.profile_id
        );
        ChatService.markConversationRead(selectedConversation.profile_id);
        // Also update the messages locally to show they're read
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            String(msg.senderId) !== String(currentUserId) && !msg.isRead
              ? { ...msg, isRead: true }
              : msg
          )
        );
      }
    }
  }, [selectedConversation, messages, currentUserId]);

  useEffect(() => {
    // Only run if we have a selected conversation and messages
    if (selectedConversation && messages.length > 0 && isConnected) {
      // Check if there are any unread messages from the other user
      const hasUnreadMessages = messages.some(
        (msg) => String(msg.senderId) !== String(currentUserId) && !msg.isRead
      );
      if (hasUnreadMessages) {
        console.log('Found unread messages in conversation, marking as read');
        // Mark the conversation as read on the server
        ChatService.markConversationRead(selectedConversation.profile_id);
        // Update the UI immediately to show messages as read
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            String(msg.senderId) !== String(currentUserId) && !msg.isRead
              ? { ...msg, isRead: true }
              : msg
          )
        );
        // Reset unread count in the conversations list
        setConversations((prevConversations) =>
          prevConversations.map((conv) =>
            String(conv.profile_id) === String(selectedConversation.profile_id)
              ? { ...conv, unreadCount: 0 }
              : conv
          )
        );
      }
    }
  }, [selectedConversation, messages, currentUserId, isConnected]);

  useEffect(() => {
    const fetchUserStatuses = async () => {
      if (!conversations.length || !isConnected || fetchingStatuses) return;

      setFetchingStatuses(true);

      try {
        // Request status for each user in our conversations
        conversations.forEach((conversation) => {
          ChatService.getUserStatus(conversation.profile_id);
        });
      } catch (err) {
        console.error('Error fetching user statuses:', err);
      } finally {
        setFetchingStatuses(false);
      }
    };

    fetchUserStatuses();
  }, [conversations, isConnected, fetchingStatuses]);

  // Enhanced periodic status checking with automatic offline detection
  useEffect(() => {
    const setUpStatusPolling = () => {
      // Clear any existing interval
      if (statusPollingInterval.current) {
        clearInterval(statusPollingInterval.current);
      }

      // Set up new polling interval - check every 30 seconds
      statusPollingInterval.current = setInterval(() => {
        if (!isConnected || conversations.length === 0) return;

        console.log('Performing periodic status update check');

        const now = Date.now();

        // 1. Check for users who might have gone offline (no activity for 5 minutes)
        const OFFLINE_THRESHOLD = 5 * 60 * 1000; // 5 minutes
        const usersToMarkOffline = Object.keys(userStatuses).filter(
          (userId) => {
            const isCurrentlyMarkedOnline = userStatuses[userId] === true;
            const lastUpdate = lastStatusUpdateTime.current[userId] || 0;
            const timeSinceLastUpdate = now - lastUpdate;

            // Mark as offline if:
            // - Currently shown as online
            // - Last update was more than threshold time ago
            return (
              isCurrentlyMarkedOnline && timeSinceLastUpdate > OFFLINE_THRESHOLD
            );
          }
        );

        // Update statuses for users who've likely gone offline
        if (usersToMarkOffline.length > 0) {
          console.log(
            'Marking users as offline due to inactivity:',
            usersToMarkOffline
          );

          // Update userStatuses state
          setUserStatuses((prev) => {
            const newStatuses = { ...prev };
            usersToMarkOffline.forEach((userId) => {
              newStatuses[userId] = false;
            });
            return newStatuses;
          });

          // Update conversations list
          setConversations((prev) =>
            prev.map((conv) => {
              if (usersToMarkOffline.includes(String(conv.profile_id))) {
                return { ...conv, isOnline: false };
              }
              return conv;
            })
          );
        }

        // 2. Request updates for users we haven't checked recently (1 minute)
        const REFRESH_THRESHOLD = 60 * 1000; // 1 minute
        const usersNeedingUpdate = conversations
          .filter((conv) => {
            const lastUpdate =
              lastStatusUpdateTime.current[conv.profile_id] || 0;
            return now - lastUpdate > REFRESH_THRESHOLD;
          })
          .map((conv) => conv.profile_id);

        // Request fresh status updates
        if (usersNeedingUpdate.length > 0) {
          console.log('Requesting status updates for:', usersNeedingUpdate);
          usersNeedingUpdate.forEach((userId) => {
            ChatService.getUserStatus(userId);
          });
        }
      }, 30000); // Every 30 seconds
    };

    // Start polling when connected
    if (isConnected) {
      setUpStatusPolling();
    }

    return () => {
      if (statusPollingInterval.current) {
        clearInterval(statusPollingInterval.current);
      }
    };
  }, [isConnected, conversations, userStatuses]);

  const handleSendMessage = (e) => {
    e.preventDefault();

    if (!newMessage.trim() || !selectedConversation) return;

    // Create a temporary message ID (will be replaced when server confirms)
    const tempId = `temp-${Date.now()}`;
    const messageText = newMessage.trim();

    // Add message to UI immediately (optimistic update)
    const newSentMessage = {
      id: tempId,
      senderId: currentUserId,
      recipientId: selectedConversation.profile_id,
      message: messageText,
      timestamp: new Date().toISOString(),
      isRead: false,
      isPending: true // Flag to show it's not yet confirmed by server
    };

    // Add to messages list immediately
    setMessages((prevMessages) => [...prevMessages, newSentMessage]);

    // Clear input field right away for better UX
    setNewMessage('');

    // Now send the actual message
    const success = ChatService.sendMessage(
      selectedConversation.profile_id,
      messageText
    );

    if (!success) {
      // If sending fails, update the message to show error state
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === tempId ? { ...msg, failed: true } : msg
        )
      );
      setError('Failed to send message. Please try again.');
    }
  };

  const handleSelectConversation = (conversation) => {
    console.log(
      'Selecting conversation:',
      conversation.profile_id,
      'Current unread count:',
      conversation.unreadCount
    );
    // Immediately update the unread count in the UI before anything else
    if (conversation.unreadCount > 0) {
      setConversations((prevConversations) =>
        prevConversations.map((conv) =>
          String(conv.profile_id) === String(conversation.profile_id)
            ? { ...conv, unreadCount: 0 }
            : conv
        )
      );
    }
    // Set the selected conversation
    setSelectedConversation(conversation);
    // Show loading state
    setLoadingMessages(true);
    // Request latest messages
    ChatService.getConversation(conversation.profile_id);
    // Mark as read on the server
    ChatService.markConversationRead(conversation.profile_id);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Retry connection
  // const handleRetryConnection = () => {
  //   if (currentUserId) {
  //     setLoading(true);
  //     setError(null);

  //     const apiUrl =
  //       process.env.REACT_APP_API_URL2 || 'https://api.samskrutimatrimony.com/';

  //     ChatService.initialize(apiUrl)
  //       .catch((err) => {
  //         setError(`Connection failed: ${err.message || 'Unknown error'}`);
  //       })
  //       .finally(() => {
  //         setLoading(false);
  //       });
  //   }
  // };

  // Utility functions
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const onCloseChat = () => {
    setSelectedConversation(null);
  };

  // Render loading state
  // if (loading) {
  //   return (
  //     <div className="chat-loading-container">
  //       <div className="chat-loader">
  //         <div className="spinner"></div>
  //         <p>Loading chat system...</p>
  //         {error && <p className="error-message">{error}</p>}
  //       </div>
  //     </div>
  //   );
  // }

  // Render connection error state
  // if (error && !isConnected) {
  //   return (
  //     <div className="chat-error-container">
  //       <div className="chat-error">
  //         <p>{error}</p>
  //         <button className="retry-button" onClick={handleRetryConnection}>
  //           Retry Connection
  //         </button>
  //       </div>
  //     </div>
  //   );
  // }

  console.log('userStatuses', userStatuses);
  return (
    <div className={styles.chatSystem}>
      {/* <img
        src={chatClose}
        alt="close"
        onClick={closeChat}
        className={styles.closeBtn}
      /> */}
      {/* {error && <div className={styles.errorMessage}>{error}</div>} */}

      {/* {connectionStatus !== 'connected' && (
        <div className={styles.connectionStatus}>
          {connectionStatus === 'connecting' ? 'Connecting...' : 'Disconnected'}
        </div>
      )} */}

      <div className={styles.chatContainer}>
        <div className={styles.conversationsList}>
          <div className={styles.ct1} onClick={closeChat}>
            <div>Conversations</div>
            {/* <div><h3>Conversations</h3></div> */}
            <div className={styles.ct2}>
              {/* <FaChevronUp onClick={oChat} /> */}
              <FaChevronDown onClick={closeChat} />
            </div>
          </div>
          {conversations?.length === 0 ? (
            <p className={styles.noConversations}>No conversations yet</p>
          ) : (
            <ul style={{ marginLeft: '-40px' }}>
              {conversations?.map((conversation) => (
                <li
                  key={conversation.profile_id}
                  className={`${styles.conversationItem} ${
                    selectedConversation?.profile_id === conversation.profile_id
                      ? styles.selected
                      : ''
                  }`}
                  onClick={() => handleSelectConversation(conversation)}
                >
                  <div className={styles.conversationAvatar}>
                    {conversation?.ProfileImages?.[0]?.objectKey ? (
                      <img
                        src={conversation.ProfileImages[0].objectKey}
                        alt={`${conversation.first_name} ${conversation.last_name}`}
                      />
                    ) : (
                      <div className={styles.defaultAvatar}>
                        {conversation?.first_name?.charAt(0)}
                      </div>
                    )}
                    {conversation.isOnline && (
                      <span className={styles.onlineIndicator} />
                    )}
                  </div>
                  <div className={styles.conversationInfo}>
                    <div className={styles.conversationHeader}>
                      {/* <span className={styles.userName}>
                        {conversation.first_name} {conversation.last_name}
                      </span> */}
                      <span className={styles.userName}>
                        {`${conversation.first_name} ${conversation.last_name}`
                          .length > 10 ? (
                          <Tooltip
                            title={`${conversation.first_name} ${conversation.last_name}`}
                            placement="top"
                          >
                            <div
                              style={{
                                display: 'inline-block',
                                cursor: 'pointer'
                              }}
                            >
                              {`${conversation.first_name} ${conversation.last_name}`.slice(
                                0,
                                15
                              )}
                              ...
                            </div>
                          </Tooltip>
                        ) : (
                          `${conversation.first_name} ${conversation.last_name}`
                        )}
                      </span>
                      <span className={styles.lastMessageTime}>
                        {formatTime(conversation?.lastMessageTime)}
                      </span>
                    </div>
                    <div className={styles.conversationPreview}>
                      <span className={styles.lastMessage}>
                        {conversation.lastMessage}
                      </span>
                      {conversation.unreadCount > 0 && (
                        <span className={styles.unreadCount}>
                          {conversation.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.chatContent}>
          {selectedConversation ? (
            <>
              <div className={styles.chatHeader}>
                <div className={styles.chatUserInfo}>
                  <div style={{ display: 'flex' }}>
                    {selectedConversation?.ProfileImages?.[0]?.objectKey ? (
                      <div>
                        <img
                          src={selectedConversation.ProfileImages[0].objectKey}
                          alt={`${selectedConversation.first_name} ${selectedConversation.last_name}`}
                          className={styles.userAvatar}
                        />
                        <div className={styles.dots}>
                          {' '}
                          {selectedConversation.isOnline ? (
                            <img
                              src={online}
                              alt="online"
                              style={{ width: '12px' }}
                            />
                          ) : (
                            <img
                              src={offline}
                              alt="offline"
                              style={{ width: '12px' }}
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className={styles.defaultAvatar}>
                        {selectedConversation?.first_name?.charAt(0)}
                      </div>
                    )}
                    {/* <div style={{ display: 'flex', gap: '30px' }}> */}
                    <div>
                      <div
                        style={{ paddingTop: '7px', color: '#d20b0b' }}
                      >{`${selectedConversation?.first_name} ${selectedConversation?.last_name}`}</div>
                      {/* <div
                                className={`${styles.status} ${
                                  selectedConversation.isOnline ? styles.online : styles.offline
                                }`}
                              >
                                {selectedConversation.isOnline ? 'Online' : 'Offline'}
                              </div> */}
                    </div>
                  </div>
                  <div>
                    <img
                      src={chatClose}
                      alt="close"
                      style={{
                        width: '15px',
                        paddingTop: '7px'
                        // paddingLeft: '161px',
                        // marginTop: '17px'
                      }}
                      onClick={onCloseChat}
                    />
                  </div>
                  {/* </div> */}
                </div>
              </div>

              <div className={styles.messagesContainer} ref={chatContainerRef}>
                {loadingMessages ? (
                  <div className={styles.loadingMessages}>
                    <div className={styles.spinnerSmall}></div>
                    <p>Loading messages...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className={styles.noMessages}>
                    No messages yet. Start a conversation!
                  </div>
                ) : (
                  <div className={styles.messagesList}>
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`${styles.message} ${
                          String(message.senderId) === String(currentUserId)
                            ? styles.sent
                            : styles.received
                        }`}
                      >
                        <div className={styles.messageContent}>
                          <p>{message.message}</p>
                          <div className={styles.messageMeta}>
                            <span className={styles.messageTime}>
                              {formatTime(message.timestamp)}
                            </span>
                            {String(message.senderId) ===
                              String(currentUserId) && (
                              <span className={styles.messageStatus}>
                                {/* {message.isRead ? '✓✓' : '✓'} */}
                                {/* {message.isRead ? (
                                  <FaCheckDouble className={styles.readIcon} />
                                ) : (
                                  <FaCheck />
                                )} */}

                                {message.isRead ? (
                                  <FaEye title="Read" />
                                ) : (
                                  <FaEyeSlash title="Sent" />
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              <form className={styles.messageForm} onSubmit={handleSendMessage}>
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className={styles.messageInput}
                  disabled={!isConnected}
                />
                <button
                  type="submit"
                  className={styles.sendButton}
                  disabled={!isConnected || !newMessage.trim()}
                >
                  {/* Send */}
                  <FiSend
                    size={20}
                    style={{ width: '17px', paddingTop: '3px' }}
                  />
                </button>
              </form>
            </>
          ) : (
            <div className={styles.noConversationSelected}>
              <p>Select a conversation to start chatting</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatSystem;
